import { Stack, Typography } from "@mui/material";

const PrivacyConditions = () => {
  return (
    <Stack spacing={2} sx={{ padding: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h1" gutterBottom>
        Privacy Notice
      </Typography>
      <Typography variant="body1">
        Middleby Corporation ("Middleby") is committed to protecting the privacy of visitors to its website. Middleby has
        implemented technical, administrative, and physical measures to safeguard any personal information that we may
        collect. This Middleby AMI Platform, including any sub-site accessible through the homepage, is operated by Middleby
        (collectively, the "Site"). This privacy notice applies to this Site only. Other Middleby websites will have separate
        privacy notices. This Site is intended for visitors who are at least 18 years of age. Middleby does not knowingly
        solicit information from, or market products or services to, children. If you are under the age of 18, do not enter
        your personal information on this Site or any other Middleby website.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h2">
        What personal information does Middleby collect?
      </Typography>
      <Typography variant="body1">
        Middleby monitors user traffic patterns throughout the Site according to a user's domain name, browser type, date and
        time of access, and pages viewed. Our web servers collect the domain names but not the e-mail addresses of visitors.
        This information is collected in order to measure the number of visitors to our Site and to determine which areas of
        the Site users find useful based on the volume of traffic. Middleby uses this information to enhance users'
        experience at the Site and to better prepare future content based on the interests of users.
      </Typography>
      <Typography variant="body1">
        To use our Site, you must register. If you register, Middleby will require that you provide your name, title, company
        name, address, email information, and telephone number. You will not be able to register without providing that
        information. You may choose to provide your mobile telephone number, photo, and language preference, but you may
        register without providing any of those three items. If you choose to register for the Site's mobile work order
        feature, Middleby will ask you for your mobile telephone number, Middleby Technician badge number, and currency
        preference. If you choose not to provide these three items, you will be unable to use the Site's mobile work order
        feature.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h2">
        How does Middleby use the personal information it collects?
      </Typography>
      <Typography variant="body1">Any personal information collected will only be used to:</Typography>
      <ul style={{ paddingLeft: "1.5rem" }}>
        <li>Provide the information, item, or service you have requested;</li>
        <li>Respond to a "contact us" inquiry;</li>
        <li>Communicate with you about products, services, and events related to Middleby;</li>
        <li>Improve our products, services, and websites, including the Site;</li>
        <li>Inquire about your views through surveys;</li>
        <li>Verify your identity to ensure security for one of the other purposes listed here;</li>
        <li>Ensure or enhance the security of Middleby's electronic systems;</li>
        <li>Protect against fraud; and</li>
        <li>Conduct investigations to ensure compliance with, and comply with, legal obligations.</li>
      </ul>
      <Typography variant="body1">
        Middleby may combine the data that you provide with other data it possesses to accomplish any of the tasks above.
        Because Middleby is a global company with locations in many different countries, we may transfer your personal
        information to the United States and may transfer your information from one legal entity to another or from one
        country to another, in order to accomplish purposes listed above. We will transfer your personal information
        consistent with applicable legal requirements and only to the extent necessary. If you do not want your information
        transferred to other legal entities or transnationally, including to countries that may not have been deemed
        "adequate" by the European Union, please do not provide us with your personal information. If you choose to provide
        us with your personal information, we will treat that action as consent to transferring your personal information to
        other companies and other countries as described in this notice.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h2">
        Does Middleby use your personal information to contact you?
      </Typography>
      <Typography variant="body1">
        Middleby may use the personal information you provide to contact you about new products, promotions, special offers,
        and other information that may be of interest to you. If you prefer not to receive such communications, please let us
        know by sending an email to <strong>amisupport@middleby.com</strong>. Also, Middleby will ensure that any marketing
        communications sent electronically will provide a simple method for you to opt-out or unsubscribe.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h2">
        Does Middleby share the information it collects with any third parties?
      </Typography>
      <Typography variant="body1">
        Middleby will not sell or otherwise share your personal information outside the Middleby family of companies, except
        to:
      </Typography>
      <ul style={{ paddingLeft: "1.5rem" }}>
        <li>
          Service providers retained to perform services on our behalf. Middleby will only share your personal information
          with service providers contractually restricted from using or disclosing the information except as necessary to
          perform services on our behalf or to comply with legal requirements;
        </li>
        <li>
          Comply with legal obligations, including in response to a legitimate legal request from law enforcement authorities
          or other government regulators;
        </li>
        <li>Investigate suspected or actual illegal activity;</li>
        <li>Prevent physical harm or financial loss; or</li>
        <li>Support the sale or transfer of all or a portion of our business or assets (including through bankruptcy).</li>
      </ul>

      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h2">
        How does Middleby use cookies or other tracking technologies?
      </Typography>
      <Typography variant="body1">
        Middleby may use cookies on this Site. Cookies are small text files sent to and stored on users' computers that allow
        websites to recognize repeat users, facilitate users' access to sites, and allow sites to compile aggregate data to
        improve content. Cookies do not damage users' computers or files. If you do not want cookies to be accessible by this
        Site or any other Middleby website, you should adjust the settings on your browser program to deny or disable the use
        of cookies. With cookies, the information collected and shared is anonymous and not personally identifiable.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h2">
        How can you access, correct, or change your personal information?
      </Typography>
      <Typography variant="body1">
        Middleby allows users to access, correct, or change information submitted to Middleby or to report problems with the
        Site, or to pose questions or concerns, by sending an email to <strong>amisupport@middleby.com</strong>. You should
        use the same contact information to revoke consent to our continued use of your personal information. If you make a
        request to review a copy of your personal information, Middleby will require proof of identity to ensure privacy
        protection in responding to access requests. Additionally, applicable law may permit Middleby to charge you a nominal
        sum to cover administrative costs in responding to your request.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h2">
        What should you understand about the third-party links that may appear on this Site?
      </Typography>
      <Typography variant="body1">
        In some instances, Middleby may provide links to non-Middleby controlled websites. However, Middleby does not control
        such third-party websites and cannot be responsible for the content or privacy practices employed by other sites.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h2">
        What additional information should Site users in California know?
      </Typography>
      <Typography variant="body1">
        Annually, California residents may request and obtain information that Middleby shared with other businesses for
        their own direct marketing use within the prior calendar year (as defined by California's "Shine the Light Law"). If
        applicable, this information would include a list of the categories of personal information that was shared and the
        names and addresses of all third parties with whom Middleby shared this information in the immediately preceding
        calendar year. To obtain this information, please send an email message to <strong>amisupport@middleby.com</strong>{" "}
        with "California Shine the Light Privacy Request" in the subject line as well as in the body of your message.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h2">
        How might Middleby change this notice?
      </Typography>
      <Typography variant="body1">
        As Middleby expands and improves this Site, we may need to update this notice. This notice may be modified from time
        to time without prior notification. We encourage you to review this notice regularly for any changes. Substantive
        changes will be identified at the top of the notice.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: "fontWeightMedium" }} component="h2">
        How can you contact Middleby?
      </Typography>
      <Typography variant="body1">
        If you have any comments or questions or if there are other things we can do to maximize the value of this Site to
        you, please email <strong>amisupport@middleby.com</strong>. If you have questions about Middleby's privacy practices
        in general, please email <strong>amisupport@middleby.com</strong>.
      </Typography>
      <Typography variant="body1">Updated by Middleby Corporation on October 30, 2024</Typography>
    </Stack>
  );
};

export default PrivacyConditions;
